import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { experimentalStyled as styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';

import { IQuestion } from '../../../classes/Question';
import { ISurvey } from '../../../classes/Survey';
import ScoreCard from '../../generic/ScoreCard';
import Grid from '@mui/material/Unstable_Grid2';
import Table from 'react-bootstrap/Table';
import './SurveyDashboardOverview.css'
import { MyProgressBar } from '../../../../helpers/funtionsUtils/funtionsUtils';
import ReturnBar from '../../generic/ReturnBar/ReturnBar';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
interface IProps {
    survey: ISurvey,
    questions: IQuestion[],
    setViewQuestionsData: Dispatch<SetStateAction<boolean>>,
    setViewComparative: Dispatch<SetStateAction<boolean>>,
    allSurveys?: any[],
    comparisonsLoaded?: boolean,
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#ffff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    //minHeight: '250px',
    }));


const SurveyDashboardOverview = (props: IProps) => {
    console.log("entra a funcion SurveyDashboardOverview:",props);
    const [comparisonsReady, setComparisonsReady] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
    
    useEffect(() => {
        // Mark comparisons as ready when the comparison loading process is complete
        // This happens when:
        // 1. Custom survey (comparisonsLoaded = true immediately with empty array)
        // 2. Template survey with previous surveys found (comparisonsLoaded = true with data)
        // 3. Template survey with NO previous surveys found (comparisonsLoaded = true with empty array)
        if (props.comparisonsLoaded) {
            setComparisonsReady(true);
        }
    }, [props.comparisonsLoaded]);
    const [dimension, setDimension] = useState("All dimensions");
    
    // Get unique dimensions from questions
    const getUniqueDimensions = () => {
        const dimensions = new Set<string>();
        props.questions.forEach(q => {
            if (q.dimension) {
                dimensions.add(q.dimension);
            }
        });
        return Array.from(dimensions);
    };
    
    const changeDimension = (event: any): void => {
        setDimension(event.target.value);
    }

    const filterQuestionsByDimension = () => {
        if (dimension !== "All dimensions") {
            console.log("caso si - Filtrando por dimension:", dimension);
            console.log("Preguntas con sus dimensiones:");
            props.questions.forEach((q, idx) => {
                console.log(`  [${idx}] "${q.title.substring(0, 50)}..." dimension="${q.dimension}"`);
            });
            const filtered = props.questions.filter(question => question.dimension === dimension);
            console.log("Preguntas filtradas:", filtered.length);
            return filtered;
        }
        else {
            console.log("caso no - Mostrando todas las preguntas", props.questions.length);
            return props.questions;
        }
    }

    const calculateSurveyAverage = (): number => {
        let total = 0;
        // If no dimensions (like eNPS after filtering), calculate average from all questions
        if (props.survey.dimensions.length === 0) {
            for(const item of props.questions) {
                total += item.score;
            }
            return total / props.questions.length - 1;
        }
        
        for(const dimension of props.survey.dimensions) {
            if(dimension.score && !Number.isNaN(dimension.score) ) {
                total += dimension.score;
            } else {
                let sum = 0;
                for(const item of props.questions) {
                    sum += item.score;
                } 
                sum /= props.questions.length;
                total += sum;
            }
        }
        return total / props.survey.dimensions.length - 1;
    }

    const calculateSurveyMaxScore = (): number => {
        // If no dimensions (like eNPS after filtering), calculate max score from questions
        if (props.survey.dimensions.length === 0) {
            const maxScoreFromQuestions = props.questions.reduce((max, question) => {
                return question.maxScore > max ? question.maxScore : max;
            }, 0);
            return maxScoreFromQuestions - 1;
        }
        
        let total = 0;
        for(const dimension of props.survey.dimensions) {
            total += dimension.maxScore;
        }
        return total - 1;
    }

    const calculateSurveyParticipation = (): number => {
        let total = 0;
        if(props.questions[0].choiceQuestionProps != undefined) {
            total = props.questions[0].choiceQuestionProps?.choiceValues.reduce((total, value) => total + value);
        }
        return total - 1;
    }

    const getPreviousSurveyComparison = (currentScore: number, dimensionName?: string): { percentage: number, trend: 'up' | 'down' | 'neutral' } | null => {
        console.log("[Comparison] Called with:", { currentScore, dimensionName });
        
        // Get native_responses from survey
        const nativeResponses = (props.survey as any).native_responses;
        if (!nativeResponses || !Array.isArray(nativeResponses) || nativeResponses.length === 0) {
            console.log("[Comparison] No native_responses available");
            return null;
        }

        // Group responses by user and timestamp (each survey completion is a session)
        // Sort responses by user and createdAt
        const sortedResponses = [...nativeResponses].sort((a, b) => {
            const userCompare = (a.nativeUserId || '').localeCompare(b.nativeUserId || '');
            if (userCompare !== 0) return userCompare;
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });

        // Group into sessions (assuming a complete survey has around 16 responses)
        const sessions: any[][] = [];
        let currentSession: any[] = [];
        let lastUserId: string | null = null;
        let lastTimestamp: number = 0;
        
        sortedResponses.forEach(response => {
            const userId = response.nativeUserId || '';
            const timestamp = new Date(response.createdAt).getTime();
            
            // If user changed or time gap > 5 minutes, start new session
            const timeDiff = Math.abs(timestamp - lastTimestamp);
            const fiveMinutes = 5 * 60 * 1000;
            
            if (userId !== lastUserId || timeDiff > fiveMinutes) {
                if (currentSession.length > 0) {
                    sessions.push(currentSession);
                }
                currentSession = [];
            }
            
            currentSession.push(response);
            lastUserId = userId;
            lastTimestamp = timestamp;
        });
        
        // Don't forget the last session
        if (currentSession.length > 0) {
            sessions.push(currentSession);
        }

        console.log("[Comparison] Total sessions found:", sessions.length);
        
        // Need at least 2 sessions to compare
        if (sessions.length < 2) {
            console.log("[Comparison] Not enough sessions to compare (need at least 2)");
            return null;
        }

        // Get the last two sessions (most recent)
        const latestSession = sessions[sessions.length - 1];
        const previousSession = sessions[sessions.length - 2];
        
        const latestDate = new Date(latestSession[0]?.createdAt).toISOString().split('T')[0];
        const previousDate = new Date(previousSession[0]?.createdAt).toISOString().split('T')[0];
        
        console.log("[Comparison] Latest session:", latestDate, "responses:", latestSession.length);
        console.log("[Comparison] Previous session:", previousDate, "responses:", previousSession.length);
        console.log("[Comparison] About to calculate scores...");

        // Calculate scores for both sessions
        const latestScore = calculateScoreForSession(latestSession, dimensionName);
        const previousScore = calculateScoreForSession(previousSession, dimensionName);
        
        console.log("[Comparison] Latest score:", latestScore, "Previous score:", previousScore);
        
        if (latestScore === null || previousScore === null) {
            console.log("[Comparison] Could not calculate scores");
            return null;
        }

        const difference = latestScore - previousScore;
        const percentage = previousScore !== 0 ? Math.abs((difference / previousScore) * 100) : 0;
        const trend: 'up' | 'down' | 'neutral' = difference > 0.01 ? 'up' : difference < -0.01 ? 'down' : 'neutral';

        const result = { percentage: Math.round(percentage), trend };
        console.log("[Comparison] Result:", result);
        return result;
    }
    
    const calculateScoreForSession = (responses: any[], dimensionName?: string): number | null => {
        console.log("[CalcScore] Input:", { responsesCount: responses.length, dimensionName });
        if (!responses || responses.length === 0) return null;
        
        // Map to convert choice values to numeric scores
        const valueMap = new Map<string, number>();
        
        // Check if we have value mappings from the survey
        if (props.survey && (props.survey as any).value_map) {
            const surveyValueMap = (props.survey as any).value_map;
            Object.keys(surveyValueMap).forEach(key => {
                valueMap.set(key, surveyValueMap[key]);
            });
            console.log("[CalcScore] Using survey value_map:", surveyValueMap);
        } else {
            // Default mappings for common scales
            // 4-point scale (Burnout)
            valueMap.set('Strongly agree', 1);
            valueMap.set('Agree', 2);
            valueMap.set('Disagree', 3);
            valueMap.set('Strongly disagree', 4);
            // 5-point scale (Engagement agreement)
            valueMap.set('Strongly Agree', 1);
            valueMap.set('Agree', 2);
            valueMap.set('Neutral', 3);
            valueMap.set('Disagree', 4);
            valueMap.set('Strongly Disagree', 5);
            // 5-point frequency scale (Employee Engagement)
            valueMap.set('Always', 5);
            valueMap.set('Often', 4);
            valueMap.set('Sometimes', 3);
            valueMap.set('Rarely', 2);
            valueMap.set('Never', 1);
            // 7-point satisfaction scale (Job Satisfaction)
            valueMap.set('Very Satisfied', 7);
            valueMap.set('Satisfied', 6);
            valueMap.set('Somewhat Satisfied', 5);
            valueMap.set('Slightly Satisfied', 5);
            valueMap.set('Neutral', 4);
            valueMap.set('Uncertain', 4);
            valueMap.set('Somewhat Dissatisfied', 3);
            valueMap.set('Slightly dissatisfied', 3);
            valueMap.set('Dissatisfied', 2);
            valueMap.set('Very Dissatisfied', 1);
            // Alternative 5-point satisfaction scale
            valueMap.set('Very satisfied', 5);
            valueMap.set('Neither satisfied nor dissatisfied', 3);
            valueMap.set('Very dissatisfied', 1);
            console.log("[CalcScore] Using default value map");
        }
        
        console.log("[CalcScore] Value map keys:", Array.from(valueMap.keys()));
        console.log("[CalcScore] Sample response:", responses[0]);
        console.log("[CalcScore] Sample response ALL FIELDS:", Object.keys(responses[0] || {}));
        console.log("[CalcScore] Sample response question_id:", responses[0]?.question_id);
        console.log("[CalcScore] Sample response questionId:", responses[0]?.questionId);
        console.log("[CalcScore] Sample response surveyQuestionId:", responses[0]?.surveyQuestionId);
        
        let filteredResponses = responses;
        
        // If dimensionName is provided, filter responses for that dimension
        if (dimensionName) {
            // Map display dimension names to internal dimension names
            const dimensionMap: {[key: string]: string} = {
                'Employee Engagement': 'Disengagement',
                'Employee Exhaustion': 'Exhaustion',
                // Employee Engagement survey dimensions (usually same name)
                'Vigor': 'Vigor',
                'Dedication': 'Dedication',
                'Absorption': 'Absorption'
            };
            const internalDimensionName = dimensionMap[dimensionName] || dimensionName;
            
            // Get question IDs for this dimension
            const dimensionQuestionIds = props.questions
                .filter(q => q.dimension === internalDimensionName)
                .map(q => q.id);
            
            console.log("[CalcScore] Display dimension:", dimensionName, "Internal:", internalDimensionName);
            console.log("[CalcScore] Dimension question IDs:", dimensionQuestionIds);
            console.log("[CalcScore] All response question_ids:", Array.from(new Set(responses.map(r => r.nativeQuestionId))));
            filteredResponses = responses.filter(r => dimensionQuestionIds.includes(r.nativeQuestionId));
            console.log("[CalcScore] Filtered to", filteredResponses.length, "responses for dimension");
        }
        
        if (filteredResponses.length === 0) {
            console.log("[CalcScore] No filtered responses!");
            return null;
        }
        
        // Calculate average score
        let total = 0;
        let count = 0;
        
        filteredResponses.forEach(response => {
            // Use 'content' field which contains the actual choice text (e.g., "Agree", "Disagree")
            const choiceValue = response.content || response.choice_value;
            if (choiceValue && valueMap.has(choiceValue)) {
                total += valueMap.get(choiceValue)!;
                count++;
            } else {
                console.log("[CalcScore] Skipping response - content:", response.content, "choice_value:", response.choice_value, "in map:", choiceValue && valueMap.has(choiceValue));
            }
        });
        
        console.log("[CalcScore] Matched", count, "out of", filteredResponses.length, "responses");
        console.log("[CalcScore] Total:", total, "Average:", count > 0 ? total / count : 0);
        
        if (count === 0) {
            console.log("[CalcScore] No matches found!");
            return null;
        }
        
        // Return score adjusted to 0-based scale (subtract 1) to match current score calculation
        const result = (total / count) - 1;
        console.log("[CalcScore] Final result:", result);
        return result;
    }

    const getSentimentCategory = (sentimentScore: number): 'positive' | 'neutral' | 'negative' => {
        if (sentimentScore > 0.66) return 'positive';
        if (sentimentScore > 0.33) return 'neutral';
        return 'negative';
    }

    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            red: parseInt(result[1], 16),
            green: parseInt(result[2], 16),
            blue: parseInt(result[3], 16)
        } : null;
    }

    const interpolateColor = (startHex: string, endHex: string, t: number): string => {
        const start = hexToRgb(startHex);
        const end = hexToRgb(endHex);
        if (!start || !end) {
            return startHex;
        }
        const result = {
            r: Math.round(start.red + (end.red - start.red) * t),
            g: Math.round(start.green + (end.green - start.green) * t),
            b: Math.round(start.blue + (end.blue - start.blue) * t),
        };
        return `rgb(${result.r}, ${result.g}, ${result.b})`;
    }

    const getENPSColor = (score: number): string => {
        // eNPS scale: 1 (most negative) -> 5 (neutral) -> 10 (most positive)
        // Colors: #FB5523 (red/orange) -> #FDD835 (yellow) -> #6DAE43 (green)
        if (score <= 1) return "#FB5523";
        if (score >= 10) return "#6DAE43";
        
        if (score <= 5) {
            // Interpolate from red to yellow (score 1-5)
            const t = (score - 1) / 4; // normalize to 0-1
            return interpolateColor("#FB5523", "#FDD835", t);
        } else {
            // Interpolate from yellow to green (score 5-10)
            const t = (score - 5) / 5; // normalize to 0-1
            return interpolateColor("#FDD835", "#6DAE43", t);
        }
    }

    const scoreColor = (score: number, maxScore: number, question?: IQuestion): string => {
        let color = "";
        console.log("score Survey Dashboard OVerview: ",score);
        console.log("question type:", question?.type, "question:", question);
        console.log("survey title:", props.survey.title);
        
        // For text questions with sentiment analysis, use sentiment-based colors
        if (question?.type === 'text' || question?.type === 'comment') {
            console.log("Using sentiment-based color for text question, score:", score);
            const sentimentCategory = getSentimentCategory(score);
            console.log("Sentiment category:", sentimentCategory);
            if(sentimentCategory === 'positive') return "#6DAE43";
            if(sentimentCategory === 'neutral') return "#FDD835";
            return "#FB5523";
        }
        
        // Note: score already comes in correct range (1-4 for rating questions, 1-10 for eNPS)
        // No need to add 1 anymore since we fixed the calculation in convertQuestions.tsx
        
        // Special handling for eNPS rating question (1-10 scale)
        if ((props.survey.title === "eNPS" || 
             props.survey.title === "Employee Net Promoter Score" ||
             props.survey.title === "eNPS (Employee Net Promoter Score) Survey") && 
            question?.type === 'rating') {
            console.log("Using eNPS color interpolation, score:", score);
            const enpsColor = getENPSColor(score);
            console.log("eNPS color:", enpsColor);
            return enpsColor;
        }
        
        switch(props.survey.title) {
          case "Employee Burnout": //Burnout - INVERTIDO: más burnout (score alto) = rojo
            if(score >= 1 && score <= 1.62) color= "#6DAE43"; // ≤1.62 = low burnout = green (poco acuerdo con síntomas)
            if(score >= 1.63 && score <= 2.67) color= "#FDD835"; // 1.63-2.67 = medium burnout = yellow
            if(score >= 2.68 && score <= 4) color= "#FB5523"; // ≥2.68 = high burnout = red (mucho acuerdo con síntomas)
            break;
          case "Employee Engagement": //Engagement
            if(score >= 1 && score <= 2.33) color= "#FB5523";
            if(score >= 2.34 && score <= 3.67) color= "#FDD835";
            if(score >= 3.67 && score <= 5) color= "#6DAE43";
            break;
          case "Job Satisfaction Survey": //Job Satisfaction
            if(score >= 1 && score <= 3) color= "#FB5523";
            if(score >= 3.1 && score <= 5.1) color= "#FDD835";
            if(score >= 5.1 && score <= 7) color= "#6DAE43";
            break;
          default:
            const percentage = score / maxScore;
            color = percentage > 0.33? percentage > 0.66? "#6DAE43" : "#FDD835" : "#FB5523";
        }
        console.log("color y score 2: ", score, color);
        return color;
      }
    const isDimesionValid = () =>{
        const myMap = new Map();
        myMap.set("Employee Engagement",1);
        myMap.set("Employee Burnout",1);
        myMap.set("Job Satisfaction Survey",1);
        myMap.set("eNPS",1);
        myMap.set("Employee Net Promoter Score",1);
        return myMap.get(props.survey.title) === 1;
    }
    const dimensionValid = isDimesionValid();
    
    const getSurveyDisplayTitle = (): string => {
        const title = props.survey.title;
        if (title === "Employee Burnout") {
            return "Employee Burnout Survey";
        }
        return title;
    }
    
    return (
        <>
            <ReturnBar title={getSurveyDisplayTitle()}/>
            <div className="cardsContainer" style={{ marginBottom: '24px' }}>
                <Grid container spacing={2.5}>
                        <Grid
                            xs={12} sm={6} md={3}
                            style={{ display: "flex", gap: "1.5rem", alignItems: "center"}}
                        >
                            <ScoreCard className="h-28"
                                text='survey'
                                score={calculateSurveyAverage()}
                                maxScore={calculateSurveyMaxScore()}
                                surveyType={props.survey.title}
                                comparison={getPreviousSurveyComparison(calculateSurveyAverage())}
                                isLoadingComparison={!comparisonsReady}
                            />
                        </Grid>
                    
                        {
                            // Only show dimension cards for template surveys (not custom surveys)
                            dimensionValid && props.survey.dimensions
                                .filter(dimension => dimension.name && dimension.name !== undefined && dimension.name !== props.survey.title)
                                .map((dimension, index) => {
                                    // Map dimension names for display
                                    const displayName = dimension.name === 'Employee Engagement' ? 'Employee Disengagement' : dimension.name;
                                    return (
                                        <Grid
                                            xs={12} sm={6} md={3}
                                            style={{ display: "flex", gap: "1.5rem", alignItems: "center"}}
                                        >
                                        <ScoreCard key={index} className="h-28"
                                            text={displayName}
                                            score={dimension.score - 1}
                                            maxScore={dimension.maxScore}
                                            surveyType={props.survey.title}
                                            comparison={getPreviousSurveyComparison(dimension.score - 1, dimension.name)}
                                            isLoadingComparison={!comparisonsReady}
                                        />
                                        </Grid>
                                    )
                                })
                        }
                     
                    <Grid
                        xs={12} sm={6} md={3}
                        style={{ display: "flex", gap: "1.5rem", alignItems: "center"}}
                    >
                        <ScoreCard className="h-28"
                            text='Employee Participation'
                            score={calculateSurveyParticipation()}
                            maxScore={calculateSurveyParticipation()}
                            surveyType={props.survey.title}
                        />
                    </Grid>
                    
                </Grid>
            </div>    

            <Item style={{ 
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #E0E0E0'
            }}>  
                <Grid container spacing={0} style={{ marginBottom: '40px', alignItems: 'center' }}>
                        <Grid xs={12} md={6}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <h3 style={{ margin: 0, fontWeight: '600', fontSize: '20px', fontFamily: 'IBM Plex Sans, sans-serif' }}>Questions</h3>
                                <span 
                                    onClick={() => props.setViewQuestionsData(true)}
                                    style={{ 
                                        color: '#1A237E', 
                                        fontSize: '16px',
                                        fontFamily: 'IBM Plex Sans, sans-serif', 
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        textDecoration: 'none'
                                    }}
                                >
                                    View all questions data <ArrowOutwardIcon style={{ fontSize: '16px' }} />
                                </span>
                            </div>
                        </Grid>
                        <Grid xs={12} md={6} style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', alignItems: 'center' }}>
                            {/* Only show List/Board view toggle for template surveys with dimensions */}
                            {dimensionValid && (
                                <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e0e0e0' }}>
                                    <button 
                                        onClick={() => setViewMode('list')}
                                        style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            background: 'none',
                                            borderBottom: viewMode === 'list' ? '2px solid #1A237E' : 'none',
                                            marginBottom: '-2px',
                                            color: viewMode === 'list' ? '#1A237E' : '#666',
                                            fontWeight: viewMode === 'list' ? '500' : '400',
                                            cursor: 'pointer'
                                        }}>
                                        List view
                                    </button>
                                    <button 
                                        onClick={() => setViewMode('board')}
                                        style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            background: 'none',
                                            borderBottom: viewMode === 'board' ? '2px solid #1A237E' : 'none',
                                            marginBottom: '-2px',
                                            color: viewMode === 'board' ? '#1A237E' : '#666',
                                            fontWeight: viewMode === 'board' ? '500' : '400',
                                            cursor: 'pointer'
                                        }}>
                                        Board view
                                    </button>
                                </div>
                            )}
                            {dimensionValid && (
                                <select 
                                    value={dimension}
                                    onChange={changeDimension}
                                    style={{
                                        padding: '10px 40px 10px 16px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                        minWidth: '240px'
                                    }}
                                >
                                    <option value="All dimensions">All dimensions</option>
                                    {
                                        getUniqueDimensions().map((dimensionName, index) => {
                                            return (
                                                <option key={index} value={dimensionName}>{dimensionName}</option>
                                            )
                                        })
                                    }
                                </select>
                            )}
                        </Grid>
                    </Grid>

                    {/* List View */}
                    {viewMode === 'list' && (
                    <Table hover className="w-full" size="sm" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                        <thead style={{ backgroundColor: 'white', borderBottom: '2px solid #1A237E' }}>
                            <tr>
                                <th style={{ 
                                    padding: '12px 0px 12px 0px', 
                                    fontWeight: '600', 
                                    fontSize: '16px',
                                    fontFamily: 'IBM Plex Sans, sans-serif', 
                                    color: '#1A237E',
                                    borderTop: 'none',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: '2px solid #1A237E'
                                }}>Question</th>
                                <th style={{ 
                                    padding: '12px 16px', 
                                    fontWeight: '600', 
                                    fontSize: '16px',
                                    fontFamily: 'IBM Plex Sans, sans-serif', 
                                    color: '#1A237E',
                                    borderTop: 'none',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: '2px solid #1A237E',
                                    textAlign: 'center'
                                }}>Results</th>
                                {dimensionValid && (
                                    <th 
                                        onClick={() => props.setViewComparative(true)} 
                                        style={{
                                            padding: '12px 16px', 
                                            fontWeight: '600', 
                                            fontSize: '16px',
                                            fontFamily: 'IBM Plex Sans, sans-serif', 
                                            color: '#1A237E',
                                            cursor: 'pointer',
                                            borderTop: 'none',
                                            borderLeft: 'none',
                                            borderRight: 'none',
                                            borderBottom: '2px solid #1A237E'
                                        }}
                                    >
                                        Dimension <ArrowOutwardIcon style={{ fontSize: '16px', verticalAlign: 'middle' }} />
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                filterQuestionsByDimension().map((question, index) => {
                                    if( Number.isNaN(question.score)  || !question.score) {
                                        question.score = 0;
                                    }
                                    console.log("paso 3,",question,question.score)
                                    let x = question.score * 20 / 100;
                                    console.log("x:",x);
                                    return (
                                        <tr key={index} style={{ 
                                            backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                                            borderBottom: '1px solid #F3F4F6'
                                        }}>
                                            <td style={{ 
                                                padding: '16px 0px 16px 0px',
                                                fontSize: '16px',
                                                fontFamily: 'IBM Plex Sans, sans-serif',
                                                color: '#1A237E',
                                                fontWeight: '500',
                                                border: 'none'
                                            }}>
                                                {index + 1}. {question.title}
                                            </td>
                                            <td style={{ 
                                                padding: '16px 20px',
                                                border: 'none',
                                                textAlign: 'center'
                                            }}>
                                            <div style={{
                                                display: 'inline-block',
                                                minWidth: '70px',
                                                padding: '8px 20px',
                                                borderRadius: '24px',
                                                backgroundColor: scoreColor(
                                                    question.score, 
                                                    question.maxScore, 
                                                    question
                                                ),
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: '16px',
                                                fontFamily: 'IBM Plex Sans, sans-serif',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                            }}>
                                                {question.score.toFixed(2)}
                                            </div>
                                            </td>
                                            {dimensionValid && (
                                                <td style={{ 
                                                    padding: '16px 20px',
                                                    fontSize: '16px',
                                                    fontFamily: 'IBM Plex Sans, sans-serif',
                                                    fontWeight: '500',
                                                    color: '#666',
                                                    border: 'none'
                                                }}>
                                                    {question.dimension}
                                                </td>
                                            )}
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </Table>
                    )}

                    {/* Board View */}
                    {viewMode === 'board' && (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '24px',
                            marginTop: '20px'
                        }}>
                            {/* Positive Values Column (Left) */}
                            <div>
                                <Table hover size="sm" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                                    <thead style={{ backgroundColor: 'white', borderBottom: '2px solid #1A237E' }}>
                                        <tr>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                fontWeight: '600', 
                                                fontSize: '16px',
                                                fontFamily: 'IBM Plex Sans, sans-serif', 
                                                color: '#1A237E',
                                                borderTop: 'none',
                                                borderLeft: 'none',
                                                borderRight: 'none',
                                                borderBottom: '2px solid #1A237E'
                                            }}>Question</th>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                fontWeight: '600', 
                                                fontSize: '16px',
                                                fontFamily: 'IBM Plex Sans, sans-serif', 
                                                color: '#1A237E',
                                                borderTop: 'none',
                                                borderLeft: 'none',
                                                borderRight: 'none',
                                                borderBottom: '2px solid #1A237E',
                                                textAlign: 'center'
                                            }}>Results</th>
                                            {dimensionValid && (
                                                <th style={{
                                                    padding: '12px 16px', 
                                                    fontWeight: '600', 
                                                    fontSize: '16px',
                                                    fontFamily: 'IBM Plex Sans, sans-serif', 
                                                    color: '#1A237E',
                                                    borderTop: 'none',
                                                    borderLeft: 'none',
                                                    borderRight: 'none',
                                                    borderBottom: '2px solid #1A237E'
                                                }}>
                                                    Dimension
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            filterQuestionsByDimension()
                                                .filter(question => {
                                                    if (Number.isNaN(question.score) || !question.score) {
                                                        question.score = 0;
                                                    }
                                                    const color = scoreColor(question.score, question.maxScore, question);
                                                    return color === '#6DAE43'; // Green = positive
                                                })
                                                .sort((a, b) => {
                                                    return a.score - b.score; // Ascending order
                                                })
                                                .map((question, index) => {
                                                    return (
                                                        <tr key={index} style={{ 
                                                            backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                                                            borderBottom: '1px solid #F3F4F6'
                                                        }}>
                                                            <td style={{ 
                                                                padding: '16px 20px',
                                                                fontSize: '16px',
                                                                fontFamily: 'IBM Plex Sans, sans-serif',
                                                                color: '#1A237E',
                                                                fontWeight: '500',
                                                                border: 'none'
                                                            }}>
                                                                {question.title}
                                                            </td>
                                                            <td style={{ 
                                                                padding: '16px 20px',
                                                                border: 'none',
                                                                textAlign: 'center'
                                                            }}>
                                                                <div style={{
                                                                    display: 'inline-block',
                                                                    minWidth: '70px',
                                                                    padding: '8px 20px',
                                                                    borderRadius: '24px',
                                                                    backgroundColor: scoreColor(question.score, question.maxScore, question),
                                                                    color: 'white',
                                                                    fontWeight: '600',
                                                                    fontSize: '16px',
                                                                    fontFamily: 'IBM Plex Sans, sans-serif',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                                                }}>
                                                                    {question.score.toFixed(2)}
                                                                </div>
                                                            </td>
                                                            {dimensionValid && (
                                                                <td style={{ 
                                                                    padding: '16px 20px',
                                                                    fontSize: '16px',
                                                                    fontFamily: 'IBM Plex Sans, sans-serif',
                                                                    fontWeight: '500',
                                                                    color: '#666',
                                                                    border: 'none'
                                                                }}>
                                                                    {question.dimension}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })
                                        }
                                    </tbody>
                                </Table>
                            </div>

                            {/* Neutral & Negative Values Column (Right) */}
                            <div>
                                <Table hover size="sm" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                                    <thead style={{ backgroundColor: 'white', borderBottom: '2px solid #1A237E' }}>
                                        <tr>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                fontWeight: '600', 
                                                fontSize: '16px',
                                                fontFamily: 'IBM Plex Sans, sans-serif', 
                                                color: '#1A237E',
                                                borderTop: 'none',
                                                borderLeft: 'none',
                                                borderRight: 'none',
                                                borderBottom: '2px solid #1A237E'
                                            }}>Question</th>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                fontWeight: '600', 
                                                fontSize: '16px',
                                                fontFamily: 'IBM Plex Sans, sans-serif', 
                                                color: '#1A237E',
                                                borderTop: 'none',
                                                borderLeft: 'none',
                                                borderRight: 'none',
                                                borderBottom: '2px solid #1A237E',
                                                textAlign: 'center'
                                            }}>Results</th>
                                            {dimensionValid && (
                                                <th style={{
                                                    padding: '12px 16px', 
                                                    fontWeight: '600', 
                                                    fontSize: '16px',
                                                    fontFamily: 'IBM Plex Sans, sans-serif', 
                                                    color: '#1A237E',
                                                    borderTop: 'none',
                                                    borderLeft: 'none',
                                                    borderRight: 'none',
                                                    borderBottom: '2px solid #1A237E'
                                                }}>
                                                    Dimension
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            filterQuestionsByDimension()
                                                .filter(question => {
                                                    if (Number.isNaN(question.score) || !question.score) {
                                                        question.score = 0;
                                                    }
                                                    const color = scoreColor(question.score, question.maxScore, question);
                                                    return color === '#FDD835' || color === '#FB5523'; // Yellow or Orange/Red = neutral/negative
                                                })
                                                .sort((a, b) => {
                                                    return a.score - b.score; // Ascending order
                                                })
                                                .map((question, index) => {
                                                    return (
                                                        <tr key={index} style={{ 
                                                            backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                                                            borderBottom: '1px solid #F3F4F6'
                                                        }}>
                                                            <td style={{ 
                                                                padding: '16px 20px',
                                                                fontSize: '16px',
                                                                fontFamily: 'IBM Plex Sans, sans-serif',
                                                                color: '#1A237E',
                                                                fontWeight: '500',
                                                                border: 'none'
                                                            }}>
                                                                {question.title}
                                                            </td>
                                                            <td style={{ 
                                                                padding: '16px 20px',
                                                                border: 'none',
                                                                textAlign: 'center'
                                                            }}>
                                                                <div style={{
                                                                    display: 'inline-block',
                                                                    minWidth: '70px',
                                                                    padding: '8px 20px',
                                                                    borderRadius: '24px',
                                                                    backgroundColor: scoreColor(question.score, question.maxScore, question),
                                                                    color: 'white',
                                                                    fontWeight: '600',
                                                                    fontSize: '16px',
                                                                    fontFamily: 'IBM Plex Sans, sans-serif',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                                                }}>
                                                                    {question.score.toFixed(2)}
                                                                </div>
                                                            </td>
                                                            {dimensionValid && (
                                                                <td style={{ 
                                                                    padding: '16px 20px',
                                                                    fontSize: '16px',
                                                                    fontFamily: 'IBM Plex Sans, sans-serif',
                                                                    fontWeight: '500',
                                                                    color: '#666',
                                                                    border: 'none'
                                                                }}>
                                                                    {question.dimension}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })
                                        }
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
            </Item>
          
        </>
    )
}

export default SurveyDashboardOverview;